import * as React from 'react';
import ViewHeaderBar from '../../components/ViewHeaderBar';
import { localizations } from '../../localization/localization';
import Grid from '@material-ui/core/Grid';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TableView from '../../components/tables/TableView';
import { Column, RowInfo } from 'react-table';
import AppliedCompanyInformation from './AppliedCompanyInformation';
import { withExpander, withHeaderStyle } from '../../components/tables/TableUtils';
import RegisteredCompanyInformation from './RegisteredCompanyInformation';
import { RegisteredCompanyModel } from '../models/CompanyModels';
import { connect } from 'react-redux';
import * as CompaniesStore from '../store/companiesStore';
import { ApplicationState } from '../../store';
import Typography from '@material-ui/core/Typography';
import { Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import customTheme from '../../common/theme';
import { momentToString } from '../../utils/dateUtilities';
import RejectedCompanyInformation from './RejectedCompanyInformation';
import Button from '@material-ui/core/Button';
import AddNotificationForm from './AddNotificationForm';
import { bindActionCreators } from 'redux';
import * as NotificationsStore from "../../notifications/notificationsStore";
import { NotificationModel } from '../../common/models/CommonModels';
import Add from '@material-ui/icons/Add';

const baseCols: Column[] = [
    {
        Header: localizations.common.companyName,
        accessor: "name",
    },
    {
        Header: localizations.common.businessId,
        accessor: "businessId"
    },
    {
        Header: localizations.common.postCode,
        id: "postCode",
        accessor: (d: RegisteredCompanyModel) => d.address ? d.address.postalCode : ""
    },
];

const appliedColumns: Column[] = withHeaderStyle(customTheme)(withExpander([
    ...baseCols,
    {
        Header: localizations.admin.registeredAt,
        id: "registeredAt",
        accessor: (d: RegisteredCompanyModel) => d.registeredAt,
        Cell: (row: any) => momentToString(row.value)
    }
]));

const registeredColumns: Column[] = withHeaderStyle(customTheme)(withExpander([
    ...baseCols,
    {
        Header: localizations.admin.acceptedAt,
        id: "acceptedAt",
        accessor: (d: RegisteredCompanyModel) => d.acceptedAt,
        Cell: (row: any) => momentToString(row.value)
    }
]));

const rejectedColumns: Column[] = withHeaderStyle(customTheme)(withExpander([
    ...baseCols,
    {
        Header: localizations.admin.rejectedAt,
        id: "rejectedAt",
        accessor: (d: RegisteredCompanyModel) => d.rejectedAt,
        Cell: (row: any) => momentToString(row.value)
    }
]));

const styles = (theme: Theme) => ({
    subHeader: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    leftIcon: {
        marginRight: theme.spacing(1),
    },
});

type WithStylesProps = "leftIcon";

enum CompaniesTab {
    Applied = "applied",
    Registered = "registered",
    Rejected = "rejected"
}

interface CompaniesViewState {
    selectedTab: CompaniesTab;
    isAddNotificationDialogOpen: boolean;
}

type CompaniesViewProps = CompaniesStore.CompaniesState & {companiesActions: typeof CompaniesStore.actionCreators, notificationsActions: typeof NotificationsStore.actionCreators } & WithStyles<WithStylesProps>;

class CompaniesView extends React.Component<CompaniesViewProps, CompaniesViewState> {

    constructor(props: any) {
        super(props);

        this.state = {
            selectedTab: CompaniesTab.Applied,
            isAddNotificationDialogOpen: false
        }

        this.handleTabChange = this.handleTabChange.bind(this);
        this.renderAppliedSubComponent = this.renderAppliedSubComponent.bind(this);
        this.renderRegisteredSubComponent = this.renderRegisteredSubComponent.bind(this);
        this.renderRejectedSubComponent = this.renderRejectedSubComponent.bind(this);
        this.handleAddNotificationClick = this.handleAddNotificationClick.bind(this);
        this.handleAddNotificationSubmit = this.handleAddNotificationSubmit.bind(this);
    }

    public componentDidMount() {
        this.props.companiesActions.requestRegisteredCompanies();
    }

    public render() {

        const { selectedTab } = this.state;
        const { data, isLoading } = this.props;


        return (
            <div>
                <AddNotificationForm 
                    open={this.state.isAddNotificationDialogOpen} 
                    onSubmit={this.handleAddNotificationSubmit} 
                    onCancel={this.handleAddNotificationClick} 
                />

                <Grid>
                    <ViewHeaderBar title={localizations.admin.companiesTitle}>
                        <Button variant="contained" color="secondary" onClick={() => this.handleAddNotificationClick()}>
                            <Add className={this.props.classes.leftIcon} />{localizations.notifications.addNotification + "..."}
                        </Button>
                    </ViewHeaderBar>


                    <Tabs value={selectedTab} onChange={this.handleTabChange}>
                        <Tab label={localizations.admin.appliedCompaniesTab} value={CompaniesTab.Applied} />
                        <Tab label={localizations.admin.registeredCompaniesTab} value={CompaniesTab.Registered} />
                        <Tab label={localizations.admin.rejectedCompaniesTab} value={CompaniesTab.Rejected} />
                    </Tabs>

                    {selectedTab === CompaniesTab.Applied && this.renderApplied(data.filter(item => !item.accepted && !item.rejected), isLoading)}
                    {selectedTab === CompaniesTab.Registered && this.renderRegistered(data.filter(item => item.accepted), isLoading)}
                    {selectedTab === CompaniesTab.Rejected && this.renderRejected(data.filter(item => !item.accepted && item.rejected), isLoading)}
                </Grid>
            </div>
        );
    }

    private handleAddNotificationClick() {
        this.setState(prevState => {
            return {
                isAddNotificationDialogOpen: !prevState.isAddNotificationDialogOpen
            }
        });
    }

    private handleAddNotificationSubmit(notification: NotificationModel) {
        this.props.notificationsActions.createNewNotification(notification);
        this.setState({isAddNotificationDialogOpen: false});
    }

    private renderApplied(data: RegisteredCompanyModel[], isLoading: boolean) {
        return <div>
            <br />
            <Typography variant="h5">{localizations.admin.appliedCompaniesTitle}</Typography>
            <br />
            <TableView data={data} columns={appliedColumns} loading={isLoading} onFetchData={this.props.companiesActions.requestRegisteredCompanies} toSubComponent={this.renderAppliedSubComponent} initialSelection={null} />
        </div >
    }

    private renderAppliedSubComponent(row: RowInfo) {
        return <AppliedCompanyInformation acceptCompany={this.props.companiesActions.acceptCompany} rejectCompany={this.props.companiesActions.rejectCompany} {...row.original} />
    }

    private renderRejected(data: RegisteredCompanyModel[], isLoading: boolean) {
        return <div>
            <br />
            <Typography variant="h5">{localizations.admin.rejectedCompaniesTitle}</Typography>
            <br />
            <TableView data={data} columns={rejectedColumns} loading={isLoading} onFetchData={this.props.companiesActions.requestRegisteredCompanies} toSubComponent={this.renderRejectedSubComponent} initialSelection={null} />
        </div >
    }

    private renderRejectedSubComponent(row: RowInfo) {
        return <RejectedCompanyInformation deleteCompany={this.props.companiesActions.deleteCompany} {...row.original} />
    }

    private renderRegistered(data: RegisteredCompanyModel[], isLoading: boolean) {
        return <div>
            <br />
            <Typography variant="h5">{localizations.admin.registeredCompaniesTitle}</Typography>
            <br />
            <TableView data={data} columns={registeredColumns} loading={isLoading} toSubComponent={this.renderRegisteredSubComponent} initialSelection={null} />
        </div>
    }
    
    private renderRegisteredSubComponent(row: RowInfo) {
        return <RegisteredCompanyInformation {...row.original} />
    }

    private handleTabChange(event: any, value: CompaniesTab) {
        this.setState({
            selectedTab: value
        });
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
      companiesActions: bindActionCreators(CompaniesStore.actionCreators, dispatch),
      notificationsActions: bindActionCreators(NotificationsStore.actionCreators, dispatch)
    }
  }
  
export default connect<any>((state: ApplicationState) => state.companies, mapDispatchToProps)(withStyles(styles)(CompaniesView));


// WEBPACK FOOTER //
// ./src/admin/companies/CompaniesView.tsx